import { Hole } from '@slope/graphql/apollo';
import { useSearchParams } from 'react-router';
import { Canvas, useThree } from '@react-three/fiber';
import Lights from '../Setup/Lights';
import styled from 'styled-components';
import { VirtualCourse, OrientedBoundingBox, Loader, Annotation } from '@slope/r3f';
import { NavMeshHelper } from '@slope/navmesh';
import * as THREE from 'three';
import { isMobile } from 'react-device-detect';
import { useStore } from '@slope/store';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CameraControls,
  GizmoHelper,
  GizmoViewport,
  Line,
  PerspectiveCamera,
  View,
} from '@react-three/drei';
import { FloatingPanelProvider } from '@slope/r3f';
import { toVec3, getOrientedBoundingBox, fitCameraToBounds, isHole } from '@slope/three';
import { getTeeColor } from '@slope/uikit';
import { NavMeshDragControls } from '@slope/r3f';
import { useControls } from 'leva';
import { OBB } from 'three/examples/jsm/Addons.js';
import { VerticalSlicer } from '../VerticalSlicer';
import { BallTrajectory, useBallTrajectoryControls } from '../BallTrajectory';
import { ParabolicLine } from '../ParabolicLine';
import { Chip } from '../Chip';
import { ChipSign } from '../ChipSign';
import { Obstacles } from '../Obstacles';
import { Reach } from '../Reach';
import { Isolines } from '../Isolines';
import { useMutableCallback } from '@slope/hooks';
import { Interactive, InteractiveHighlight } from '../Interactive';
import { HoleCameraControls, HoleControls, isHoleCameraControls } from '../HoleControls';
import { SurfaceMesh } from '../SurfaceMesh';
import { Isogrid } from '../Isogrid';

const halfExtents = /*@__PURE__*/ new THREE.Vector3(50, 50, 50);
const v1 = /*@__PURE__*/ new THREE.Vector3();
const v2 = /*@__PURE__*/ new THREE.Vector3();

/**
 * Traverse the hierarchy in DFS order, and return the first object
 * for which `test(obj)` is truthy.
 * If none match, returns null.
 */
function traverseUntil(
  object: THREE.Object3D,
  test: (obj: THREE.Object3D) => boolean,
): THREE.Object3D | null {
  if (test(object)) return object;

  for (const child of object.children) {
    const match = traverseUntil(child, test);
    if (match) return match;
  }

  return null;
}

const getElementByRegex = (object: THREE.Object3D, regex: RegExp): THREE.Object3D | null => {
  return traverseUntil(object, (obj) => obj.visible && regex.test(obj.name));
};

const annotations =
  // @ts-ignore
  true
    ? []
    : ([
        {
          id: 'b2ae757a-1f5f-42f4-b04e-24dd137823f5',
          hole: {
            number: 7,
          },
          content:
            '### Jon Rahm Shot LIV Golf Valderrama 2024 trees\n::video{src=/videos/jon-rahm-shot-liv-golf-valderrama-2024-trees.webm}',
          coordinates: {
            latitude: 36.28553584438355,
            longitude: -5.329842452221523,
          },
          position: [-376.8506164550781, 61.75685501098633, -309.78851318359375],
          updatedAt: '2025-06-18T03:02:51.519398+00:00',
          createdAt: '2025-06-18T03:02:51.519398+00:00',
        },
        {
          id: 'f2448f21-6375-4d56-8107-cf533db9934a',
          hole: {
            number: 7,
          },
          content:
            '### Bryson DeChambeau eagles the final hole to take LIV Golf lead in Spain.\n::video{src=/videos/bryson-dechambeau-eagles-final-hole.webm}',
          coordinates: {
            latitude: 36.2842528076445,
            longitude: -5.329069150638745,
          },
          position: [-367.961669921875, 59.419410705566406, -126.69950103759766],
          updatedAt: '2025-06-18T03:02:51.519398+00:00',
          createdAt: '2025-06-18T03:02:51.519398+00:00',
        },
        {
          id: 'b2ae757e-1f5f-43f4-b04e-24dd137821f5',
          hole: {
            number: 1,
          },
          content:
            '### Jon Rahm Shot LIV Golf Valderrama 2024 trees\n::video{src=/videos/jon-rahm-shot-liv-golf-valderrama-2024-trees.webm}',
          coordinates: {
            latitude: 36.28553584438355,
            longitude: -5.329842452221523,
          },
          position: [-155.8439178466797, 68.21044921875, -386.59246826171875],
          updatedAt: '2025-06-18T03:02:51.519398+00:00',
          createdAt: '2025-06-18T03:02:51.519398+00:00',
        },
        {
          id: 'f2461f21-6375-4d56-8107-cf533db9934a',
          hole: {
            number: 1,
          },
          content:
            '### Bryson DeChambeau eagles the final hole to take LIV Golf lead in Spain.\n::video{src=/videos/bryson-dechambeau-eagles-final-hole.webm}',
          coordinates: {
            latitude: 36.2842528076445,
            longitude: -5.329069150638745,
          },
          position: [-93.44515228271484, 65.22588348388672, -354.63555908203125],
          updatedAt: '2025-06-18T03:02:51.519398+00:00',
          createdAt: '2025-06-18T03:02:51.519398+00:00',
        },
        {
          id: 'b00ed999-b81f-459d-a6c8-3d1ad46f1748',
          hole: {
            number: 1,
          },
          content:
            '### Nicolas Colsaerts 9th tee shot Valderrama\n::video{src=/videos/nicolas-colsaerts-9th-tee-shot-valderrama.webm}',
          coordinates: {
            latitude: 36.28481879890483,
            longitude: -5.329586427593974,
          },
          position: [-56.77067184448242, 61.99711227416992, -297.8097229003906],
          updatedAt: '2025-06-18T03:02:51.519398+00:00',
          createdAt: '2025-06-18T03:02:51.519398+00:00',
        },
      ] satisfies {
        id: string;
        content: string;
        hole: {
          number: number;
        };
        coordinates: {
          latitude: number;
          longitude: number;
        };
        position: [number, number, number];
        updatedAt: string;
        createdAt: string;
      }[]);

enum ViewMode {
  Hole = 1,
  Green = 2,
}

type SceneProps = {
  onLoad?: (object: THREE.Object3D) => void;
  controls?: boolean;
  hole: Pick<Hole, 'number' | 'par'>;
  clubSlug: string;
};

const Scene: React.FC<SceneProps> = ({ onLoad, controls: controlsEnabled = true, hole, clubSlug }) => {
  const lngLatToScene = useStore(({ lngLatToScene }) => lngLatToScene);
  const storeSet = useStore(({ set }) => set);
  const scene = useStore(({ scene }) => scene);
  const playerTee = useStore(({ playerTee }) => playerTee);
  const controls = useThree(({ controls }) => controls);
  const camera = useThree(({ camera }) => camera);
  const controlApiRef = useRef<HoleCameraControls | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Hole);

  // Construct dynamic model URL based on club slug
  const modelUrl = `https://resources.nextcaddy.com/clubResources/${clubSlug}/orbital/model.glb`;

  const teePos = useMemo(() => {
    const tee = getElementByRegex(scene, new RegExp(`_tee$`));
    return new THREE.Box3().setFromObject(tee ?? scene).getCenter(new THREE.Vector3());
  }, [hole.number, lngLatToScene, scene]);

  const greenPos = useMemo(() => {
    const green = getElementByRegex(scene, new RegExp(`_green$`));
    return new THREE.Box3().setFromObject(green ?? scene).getCenter(new THREE.Vector3());
  }, [hole.number, lngLatToScene, scene]);

  const handleLoad = useCallback((object: THREE.Object3D) => {
    onLoad?.(object);
    storeSet({ scene: object });
  }, []);

  const direction = useMemo(() => {
    return new THREE.Vector3().subVectors(toVec3(greenPos), toVec3(teePos)).normalize();
  }, [greenPos, teePos]);

  const obb = useMemo(() => {
    const obb = new OBB();
    if (!scene) return obb;

    const defaultForward = new THREE.Vector3(0, 0, 1);
    const rotation = new THREE.Quaternion().setFromUnitVectors(defaultForward, direction);

    return getOrientedBoundingBox(scene, rotation);
  }, [scene, teePos, greenPos, direction]);

  const { position, lookAt, orientation } = useMemo(() => {
    if (isHoleCameraControls(controls)) {
      const { camera } = controls;

      const box = new THREE.Box3().setFromCenterAndSize(
        obb.center,
        obb.halfSize.clone().multiplyScalar(2),
      );
      const orientation = toVec3(teePos).clone().sub(toVec3(greenPos)).normalize();
      const target = box.getCenter(new THREE.Vector3());
      const position = fitCameraToBounds(box, camera, 0.8);

      return {
        position,
        lookAt: target,
        orientation,
      };
    }

    return {
      position: new THREE.Vector3(),
      lookAt: new THREE.Vector3(),
      orientation: new THREE.Vector3(),
    };
  }, [controls, scene]);

  const { curve, curvePoints, tracker, trackerPoints } = useMemo(() => {
    //const fairway = getElementByRegex(scene, /fairway/);
    const fairway = null;
    
    // Try to find midPath element, fallback to midpoint between tee and green
    const midPathElement = getElementByRegex(scene, /midPath_/);
    const midPathPos = midPathElement
      ? new THREE.Box3().setFromObject(midPathElement).getCenter(new THREE.Vector3())
      : new THREE.Vector3().lerpVectors(toVec3(teePos), toVec3(greenPos), 0.5);
    
    const direction = new THREE.Vector3().subVectors(toVec3(greenPos), toVec3(teePos)).normalize();
    // Increase camera height for better separation
    const pathHeight = 80;
    const pathHeighVector = new THREE.Vector3(0, pathHeight, 0);
    const length = toVec3(greenPos).distanceTo(toVec3(teePos));
    
    // Camera curve - higher and further back from tee/green with center point
    const curve = new THREE.CatmullRomCurve3(
      [
        position,
        toVec3(teePos)
          .add(pathHeighVector)
          .sub(direction.clone().multiplyScalar(length * 0.35)), // More back offset from tee
        midPathPos.clone().add(pathHeighVector), // Add center point
        toVec3(greenPos)
          .add(pathHeighVector)
          .sub(direction.clone().multiplyScalar(length * 0.15)), // More back offset from green
      ].filter(Boolean) as THREE.Vector3[],
      false,
      'chordal',
      100,
    );
    
    // Tracker curve - stays lower and closer to ground targets with center point
    const tracker = new THREE.CatmullRomCurve3(
      [
        obb.center.add(direction.clone().multiplyScalar(length * 0.02)),
        toVec3(teePos).add(new THREE.Vector3(0, 5, 0)), // Slight elevation for smoother tracking
        midPathPos.clone().add(new THREE.Vector3(0, 5, 0)), // Add center point with slight elevation
        toVec3(greenPos).add(new THREE.Vector3(0, 5, 0)), // Slight elevation for smoother tracking
      ].filter(Boolean) as THREE.Vector3[],
      false,
      'chordal',
    );
    return {
      curve,
      curvePoints: curve.getPoints(50),
      tracker,
      trackerPoints: tracker.getPoints(50),
      scene,
    };
  }, [teePos, greenPos, position, obb]);

  const [queryParams] = useSearchParams();

  const { showOBB, showCameraControls, showIsolines } = useControls(
    'Helpers',
    {
      showOBB: false,
      showCameraControls: false,
      showIsolines: queryParams.has('isolines'),
    },
    { collapsed: false },
  );

  useEffect(() => {
    if (!isHoleCameraControls(controls)) {
      return;
    }

    const target = obb.center;

    controls.setLookAt(position.x, position.y, position.z, target.x, target.y, target.z, true);
    const azimuthAngle = Math.atan2(orientation.x, orientation.z);

    controls.rotateAzimuthTo(azimuthAngle, false);

    controls.saveState();
  }, [scene, position, lookAt, controls]);

  const cameraControls = useControls('Camera Controls Settings', {
    maxPolarAngle: {
      value: Math.PI / 2,
      min: 0,
      max: Math.PI,
      step: 0.01,
    },
  });

  const [chipPos, setChipPos] = useState<THREE.Vector3>(() => teePos.clone());

  const navMesh = useMemo(() => {
    return isHole(scene) ? scene.navMesh : null;
  }, [scene]);

  const greenDistance = useMemo(
    () => toVec3(chipPos, v1).distanceTo(toVec3(greenPos, v2)),
    [chipPos, greenPos],
  );

  const teeDistance = useMemo(
    () => toVec3(chipPos, v1).distanceTo(toVec3(teePos, v2)),
    [chipPos, teePos],
  );

  const chipInitialPos = useMemo(() => {
    const greenTeeDistance = toVec3(greenPos, v1).distanceTo(toVec3(teePos, v2));
    return (
      greenTeeDistance < 240
        ? obb.center
        : toVec3(teePos).add(direction.clone().multiplyScalar(240))
    ).toArray() as THREE.Vector3Tuple;
  }, [teePos, greenPos, direction]);

  const annotationNodes = useMemo(
    () =>
      annotations.map(({ id, position, coordinates, content, hole }) => (
        <Annotation
          key={`hole_${hole.number}_${id}`}
          zIndexRange={[0, 10]}
          position={
            position ??
            lngLatToScene({
              lat: coordinates.latitude,
              lng: coordinates.longitude,
            })
          }
          children={content}
          visible={hole.number === hole.number}
        />
      )),
    [lngLatToScene, hole.number],
  );

  const isolinesProps = useControls('Isolines', {
    animationSpeed: {
      value: 0.12,
      min: 0,
      max: 1,
      step: 0.001,
    },
    opacity: {
      value: 0.25,
      min: 0,
      max: 1,
      step: 0.01,
    },

    lineDensity: {
      value: 22,
      min: 1,
      max: 100,
      step: 1,
    },
    lineThickness: {
      value: 1.9,
      min: 0.1,
      max: 10,
      step: 0.1,
    },
  });

  const isogridProps = useControls('Isogrid', {
    animationSpeed: {
      value: 0,
      min: 0,
      max: 1,
      step: 0.001,
    },
    opacity: {
      value: 0.15,
      min: 0,
      max: 1,
      step: 0.01,
    },
    lineDensity: {
      value: 2,
      min: 1,
      max: 4,
      step: 1,
    },
    lineThickness: {
      value: 0.9,
      min: 0.1,
      max: 10,
      step: 0.1,
    },
    theta: {
      value: 0,
      min: 0,
      max: Math.PI * 2,
      step: 0.01,
    },
    color: { value: '#ffffff' },
  });

  const reachLineProps = useControls('ReachLines', {
    opacity: {
      value: 0.3,
      min: 0,
      max: 1,
      step: 0.01,
    },
    lineThickness: {
      value: 0.25,
      min: 0.01,
      max: 1,
      step: 0.01,
    },
  });

  const handleDragChange = useMutableCallback((pos: THREE.Vector3Tuple) => {
    const posVec = new THREE.Vector3().fromArray(pos);
    const cameraDistance = camera.position.distanceTo(posVec);
    if (cameraDistance < 60) {
      controlApiRef.current?.travelBy(-0.001, true);
    } else if (cameraDistance > 100 && cameraDistance < 200) {
      controlApiRef.current?.travelBy(+0.001, true);
    }

    setChipPos(posVec);
  });

  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useMutableCallback(() => {
    if (viewMode === ViewMode.Green) {
      setViewMode(ViewMode.Hole);
      controlApiRef.current?.trackOn(chipPos, true);
    }
    setIsDragging(true);
  });

  const handleDragEnd = useMutableCallback(() => {
    setIsDragging(false);
  });

  const handleChipClick = useMutableCallback(() => {
    if (viewMode === ViewMode.Green) {
      setViewMode(ViewMode.Hole);
      controlApiRef.current?.trackOn(chipPos, true);
    }
  });

  const green = useMemo(() => getElementByRegex(scene, /_green$/), [scene]);
  const semi = useMemo(() => getElementByRegex(scene, /_semi$/), [scene]);

  useEffect(() => {
    setViewMode(ViewMode.Hole);
  }, [hole.number]);

  return (
    <>
      <Suspense fallback={<Loader message="Loading course..." />}>
        <VirtualCourse src={modelUrl} hole={`hole_${hole.number}`} onLoad={handleLoad} />

        <HoleControls
          {...cameraControls}
          key={hole.number}
          ref={controlApiRef}
          curve={curve}
          tracker={tracker}
          makeDefault
          smoothTime={0.5}
          orbital={viewMode === ViewMode.Green}
        />
        <Lights greenPos={greenPos} />
        {viewMode === ViewMode.Hole && <ParabolicLine startPoint={chipPos} endPoint={greenPos} />}
        <ChipSign
          color={getTeeColor(playerTee.color)}
          transform
          sprite
          distanceFactor={50}
          position={chipPos}
          position-y={chipPos.y + 7 + (isDragging ? (isMobile ? 10 : 3) : 0)}
          teeDistance={teeDistance}
          greenDistance={greenDistance}
          zIndexRange={[200, 201]}
          aside={hole.par !== 3}
        />
        <FloatingPanelProvider>
          {viewMode === ViewMode.Hole && <Obstacles teePos={teePos} greenPos={greenPos} />}
        </FloatingPanelProvider>
      </Suspense>

      {green && (
        <Interactive
          key={green.uuid}
          object={green}
          highlightMask={InteractiveHighlight.Near | InteractiveHighlight.Hovered}
          iconMask={InteractiveHighlight.Near | InteractiveHighlight.Hovered}
          onClick={async () => {
            setViewMode(ViewMode.Green);
            controlApiRef.current?.focusOn(greenPos, true);
          }}
          visible={viewMode === ViewMode.Hole}
        />
      )}

      {semi && (
        <Interactive
          key={semi.uuid}
          object={semi}
          onClick={async () => {
            setViewMode(ViewMode.Hole);
            controlApiRef.current?.trackOn(chipPos, true);
          }}
          highlightOpacity={[0.65, 0.7]}
          visible={viewMode === ViewMode.Green}
        />
      )}

      {viewMode === ViewMode.Hole && annotationNodes}

      {navMesh && (
        <NavMeshDragControls
          navMesh={navMesh}
          halfExtents={halfExtents}
          onChange={handleDragChange}
          position={chipInitialPos}
          spring={false}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Chip onClick={handleChipClick} />
        </NavMeshDragControls>
      )}

      {scene && (
        <SurfaceMesh
          object={scene}
          visible={showIsolines || viewMode === ViewMode.Green}
          layers={viewMode === ViewMode.Hole ? ['green', 'fairway'] : undefined}
        >
          <meshBasicMaterial visible={false} />
          <Isolines {...isolinesProps} />
        </SurfaceMesh>
      )}

      {scene && (
        <SurfaceMesh
          object={scene}
          visible={viewMode === ViewMode.Green}
          layers={viewMode === ViewMode.Hole ? ['green'] : undefined}
        >
          <meshBasicMaterial visible={false} />
          <Isogrid
            {...isogridProps}
            theta={isogridProps.theta + Math.atan2(direction.x, direction.z)}
          />
        </SurfaceMesh>
      )}

      {navMesh && (
        <NavMeshHelper key={scene.uuid} navMesh={navMesh}>
          <meshBasicMaterial visible={false} />

          <Reach
            center={chipPos}
            radius={20}
            color="white"
            {...reachLineProps}
            visible={viewMode === ViewMode.Hole}
          />
          <Reach
            center={greenPos}
            radius={100}
            color="red"
            {...reachLineProps}
            visible={viewMode === ViewMode.Hole}
          />
          <Reach
            center={greenPos}
            radius={50}
            color="blue"
            {...reachLineProps}
            visible={viewMode === ViewMode.Hole}
          />
          <Reach
            center={teePos}
            radius={250}
            color="black"
            {...reachLineProps}
            visible={viewMode === ViewMode.Hole}
          />
          <Reach
            center={teePos}
            radius={200}
            color="black"
            {...reachLineProps}
            visible={viewMode === ViewMode.Hole}
          />
        </NavMeshHelper>
      )}

      {showCameraControls && curvePoints.length && (
        <Line points={curvePoints} color="hotpink" lineWidth={2} />
      )}

      {showCameraControls && trackerPoints.length && (
        <Line points={trackerPoints} color="yellow" lineWidth={2} />
      )}

      {false && <ParabolicLine startPoint={teePos} endPoint={greenPos} />}

      {false && (
        <BallTrajectory position={teePos} direction={direction} {...useBallTrajectoryControls()} />
      )}

      {showOBB && <OrientedBoundingBox box={obb} color="cyan" />}

      {/* <box3Helper args={[new THREE.Box3().setFromObject(scene, true), 'magenta']} /> */}
      {/*   <box3Helper
        rotation={new THREE.Euler().setFromQuaternion(
          new THREE.Quaternion().setFromRotationMatrix(
            new THREE.Matrix4().setFromMatrix3(obb.rotation),
          ),
        )}
        args={[
          new THREE.Box3().setFromCenterAndSize(obb.center, obb.halfSize.clone().multiplyScalar(2)),
          'green',
        ]}
      /> */}
    </>
  );
};

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  /* Prevent iOS scroll/refresh behaviors */
  touch-action: none;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: auto;
`;

export type ViewerProps = SceneProps & {};

export const Viewer: React.FC<ViewerProps> = ({ clubSlug, ...sceneProps }) => {
  const [object, setObject] = useState<THREE.Object3D | null>(null);

  const containerRef = useRef<HTMLDivElement>(null!);
  const cameraRef = useRef<THREE.PerspectiveCamera>(null!);
  const scene = useStore(({ scene }) => scene);
  const lngLatToScene = useStore(({ lngLatToScene }) => lngLatToScene);

  const { splitView } = useControls('Split view', {
    splitView: {
      value: false,
    },
  });

  const { navMeshFlags } = useControls('NavMesh Helper Options', {
    navMeshFlags: {
      label: 'Marker Flags',
      value: 0xffff,
      options: {
        All: 0xffff,
        Green: 1 << 1,
        Tee: 1 << 2,
        Bunker: 1 << 3,
        Semi: 1 << 4,
        Fairway: 1 << 5,
        Rough: 1 << 6,
        Path: 1 << 7,
      },
    },
  });

  const { cameraHelper, navMeshHelper, surfaceMeshHelper, axisHelper } = useControls(
    'Helpers',
    {
      cameraHelper: {
        value: true,
      },
      surfaceMeshHelper: {
        value: false,
      },
      navMeshHelper: {
        value: false,
      },
      axisHelper: {
        value: false,
      },
    },
    { collapsed: true },
  );

  const mainView = (
    <>
      {surfaceMeshHelper && isHole(scene) && (
        <NavMeshHelper navMesh={scene.navMesh}>
          <meshBasicMaterial color="green" transparent opacity={0.5} depthTest={false} />
        </NavMeshHelper>
      )}
      {navMeshHelper && isHole(scene) && (
        <NavMeshHelper navMesh={scene.navMesh} flags={navMeshFlags} />
      )}
      <PerspectiveCamera
        ref={cameraRef}
        position={[0, 0, 100]}
        fov={90}
        near={1}
        far={1000}
        makeDefault
      />
      <Scene onLoad={setObject} clubSlug={clubSlug} {...sceneProps} />
      {axisHelper && (
        <GizmoHelper renderPriority={2}>
          <GizmoViewport />
        </GizmoHelper>
      )}
    </>
  );

  return (
    <Container ref={containerRef}>
      {splitView && (
        <VerticalSlicer
          initialLeftPercent={65}
          left={
            <View index={1} style={{ width: '100%', height: '100%' }}>
              {mainView}
            </View>
          }
          right={
            <View index={2} style={{ width: '100%', height: '100%' }}>
              <PerspectiveCamera
                position={[1000, 1000, 1000]}
                fov={45}
                near={1}
                far={10000}
                makeDefault
              />
              <VirtualCourse src={`https://resources.nextcaddy.com/clubResources/${clubSlug}/orbital/model.glb`} hole="all" />
              <Lights />
              <CameraControls makeDefault />
              {axisHelper && (
                <GizmoHelper renderPriority={3}>
                  <GizmoViewport />
                </GizmoHelper>
              )}
            </View>
          }
        />
      )}

      <Canvas
        eventSource={containerRef}
        gl={{
          antialias: true,
          powerPreference: 'high-performance',
          alpha: true,
          stencil: false,
          preserveDrawingBuffer: true,
        }}
        style={{
          pointerEvents: 'none',
          position: 'absolute',
          inset: 0,
        }}
      >
        {splitView ? <View.Port /> : mainView}
      </Canvas>
    </Container>
  );
};
